import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useAuth } from "../../contexts/AuthContext";
import Card from "../shared/Card/Card";
import Modal from "../../layouts/Modal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { SearchPaletteItem } from "../shared/Inputs/SearchPaletteTypes";
import BulkAddToWorkOrderModal from "./BulkAddToWorkOrderModal";
import WorkOrderForm from "./WorkOrderForm";
import useWorkOrderMutations from "../../data/useWorkOrderMutations";
import { ICreateWorkOrderRequest, IWorkOrder, IWorkOrdersResponse, WorkOrderStatus } from "../../models/WorkOrder";
import useApiHelper from "../../hooks/useApiHelper";
import apiConfig from "../../utils/apiConfig";

interface LinkedWorkOrdersCardProps {
  linkedItems: SearchPaletteItem[];
  title?: string;
}

export default function LinkedWorkOrdersCard({ linkedItems, title = "Work Orders" }: LinkedWorkOrdersCardProps) {
  const { userId } = useAuth();
  const { update } = useWorkOrderMutations();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<IWorkOrder | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const { get } = useApiHelper(apiConfig("LinkedWorkOrdersCard"));
  const primaryLinkedItem = linkedItems[0];

  const workOrdersQuery = useQuery({
    queryKey: ["linkedWorkOrdersCard", primaryLinkedItem?.id, primaryLinkedItem?.type, debouncedSearch],
    enabled: Boolean(primaryLinkedItem?.id),
    queryFn: async () => {
      if (!primaryLinkedItem?.id) return [];

      const queryParams = new URLSearchParams();
      queryParams.set("take", "20");
      queryParams.set("includeCompleted", "false");
      if (debouncedSearch.trim()) queryParams.set("query", debouncedSearch.trim());

      const mappedType = mapSearchTypeToApiWorkOrderItemType(primaryLinkedItem.type);
      if (mappedType) queryParams.set("type", mappedType);

      const response = await get<IWorkOrdersResponse>(
        `/workorder/by-linked/${primaryLinkedItem.id}?${queryParams.toString()}`
      );

      return (response.data ?? []).slice(0, 20);
    },
    staleTime: 1000 * 30,
  });
  const linkedWorkOrders: IWorkOrder[] = workOrdersQuery.data ?? [];

  return (
    <>
      <BulkAddToWorkOrderModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        selectedItems={linkedItems}
      />
      <Modal open={isEditOpen} setOpen={setIsEditOpen} width="max-w-5xl">
        <WorkOrderForm
          workOrder={selectedWorkOrder}
          createdById={userId ?? null}
          onCancel={() => setIsEditOpen(false)}
          isSubmitting={update.isLoading}
          onSubmit={(request: ICreateWorkOrderRequest) => {
            if (!selectedWorkOrder?.id) return;
            update.mutate({ workOrderId: selectedWorkOrder.id, request });
          }}
          onComplete={() => setIsEditOpen(false)}
          initialLinkedItems={[]}
        />
      </Modal>
      <Card
        title={title}
        headerRight={
          <ButtonInput
            label="Add to work order"
            isSubmit={false}
            onClick={(e: any) => {
              e?.stopPropagation?.();
              setIsAddModalOpen(true);
            }}
            classes="!py-1 !px-3 !text-sm"
          />
        }
        bodyClassName="p-4 space-y-3"
      >
        {/* <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search work orders..."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        /> */}

        <div className="space-y-2 max-h-72 overflow-auto">
          {workOrdersQuery.isLoading && <p className="text-sm text-gray-500">Loading work orders...</p>}
          {!workOrdersQuery.isLoading && linkedWorkOrders.length === 0 && (
            <p className="text-sm text-gray-500">No linked work orders.</p>
          )}

          {linkedWorkOrders.map((workOrder) => (
            <button
              key={workOrder.id}
              type="button"
              onClick={() => {
                setSelectedWorkOrder(workOrder);
                setIsEditOpen(true);
              }}
              className="w-full text-left rounded-md border border-gray-200 p-3 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {workOrder.orderNumber} - {workOrder.title}
                </p>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getStatusClasses(workOrder.status)}`}>
                  {formatWorkOrderStatus(workOrder.status)}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {(workOrder.workOrderItems ?? []).length} linked item{(workOrder.workOrderItems ?? []).length === 1 ? "" : "s"}
              </p>
            </button>
          ))}
        </div>
      </Card>
    </>
  );
}

function mapSearchTypeToApiWorkOrderItemType(type?: SearchPaletteItem["type"]): string | null {
  switch (type) {
    case "Order":
      return "Order";
    case "Vehicle":
      return "Vehicle";
    case "Stock":
      return "Stock";
    default:
      return null;
  }
}

function formatWorkOrderStatus(status: WorkOrderStatus): string {
  switch (status) {
    case WorkOrderStatus.ToDo:
      return "To Do";
    case WorkOrderStatus.InProgress:
      return "In Progress";
    case WorkOrderStatus.Complete:
      return "Complete";
    default:
      return "Unknown";
  }
}

function getStatusClasses(status: WorkOrderStatus): string {
  switch (status) {
    case WorkOrderStatus.ToDo:
      return "bg-gray-100 text-gray-700";
    case WorkOrderStatus.InProgress:
      return "bg-blue-100 text-blue-700";
    case WorkOrderStatus.Complete:
      return "bg-green-100 text-green-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}
